import React, { useState } from "react";

import { useController, useFormContext } from "react-hook-form";

import { Input } from "@/src/components/ui/input";
import { ToggleGroup, ToggleGroupItem } from "@/src/components/ui/toggle-group";
import { cn } from "@/src/lib/utils";

import { SEGMENT_ITEM_CLASS, SEGMENT_TRACK_CLASS } from "./SegmentedField";
import { Field, FieldLabel } from "../ui/field";

import type { ChangeEvent, JSX, ReactNode } from "react";
import type { FieldPathByValue, FieldValues } from "react-hook-form";

interface PillFieldBaseProps {
  label?: string;
  value: number;
  presets: number[];
  allowCustom?: boolean;
  disabled?: boolean;
  formatLabel?: (value: number) => ReactNode;
  placeholder?: string;
  onValueChange: (value: number) => void;
}

/**
 * Segmented preset picker whose trailing cell is a free-form number input
 * living inside the same track. Selecting a preset emits it and clears the
 * input; typing a positive value emits it and deselects the presets.
 */
export function PillFieldBase({
  label,
  value,
  presets,
  allowCustom = true,
  disabled,
  formatLabel,
  placeholder,
  onValueChange,
}: PillFieldBaseProps): JSX.Element {
  const isCustom = !presets.includes(value);
  const [customValue, setCustomValue] = useState<number | "">(
    isCustom ? value : "",
  );

  const handleSelect = (next: string): void => {
    if (!next) return;
    setCustomValue("");
    onValueChange(Number(next));
  };

  return (
    <Field>
      {label ? <FieldLabel>{label}</FieldLabel> : null}
      <div className={SEGMENT_TRACK_CLASS}>
        <ToggleGroup
          className="flex flex-1 gap-1"
          disabled={disabled}
          onValueChange={handleSelect}
          spacing={1}
          type="single"
          value={isCustom ? "" : String(value)}
        >
          {presets.map((preset) => (
            <ToggleGroupItem
              key={preset}
              className={SEGMENT_ITEM_CLASS}
              value={String(preset)}
            >
              {formatLabel ? formatLabel(preset) : preset}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
        {allowCustom ? (
          <Input
            disabled={disabled}
            placeholder={placeholder}
            type="number"
            value={customValue}
            className={cn(
              "h-8 w-24 shrink-0 rounded-md border-0 bg-transparent text-center text-sm shadow-none focus-visible:ring-0",
              isCustom && "bg-card text-primary font-medium",
            )}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              const next = Number(event.target.value);
              setCustomValue(event.target.value === "" ? "" : next);
              if (next > 0) onValueChange(next);
            }}
          />
        ) : null}
      </div>
    </Field>
  );
}

interface PillFieldProps<T extends FieldValues> {
  name: FieldPathByValue<T, number | undefined>;
  presets: number[];
  label?: string;
  allowCustom?: boolean;
  disabled?: boolean;
  placeholder?: string;
  formatLabel?: (value: number) => ReactNode;
}

/** `PillField` bound to a react-hook-form numeric field via `useController`. */
export function PillField<T extends FieldValues>({
  name,
  presets,
  label,
  allowCustom,
  disabled,
  placeholder,
  formatLabel,
}: PillFieldProps<T>): JSX.Element {
  const { control } = useFormContext();
  const { field } = useController({ control, name });

  return (
    <PillFieldBase
      allowCustom={allowCustom}
      disabled={disabled}
      formatLabel={formatLabel}
      label={label}
      onValueChange={field.onChange}
      placeholder={placeholder}
      presets={presets}
      value={field.value ?? 0}
    />
  );
}
